<template>
  <div class="withdrawal-list cf-list cf-list--gold">
    <!-- Section Title -->
    <header class="cf-list__head">
      <span class="cf-list__icon">
        <v-icon size="22">mdi-trophy-variant-outline</v-icon>
      </span>
      <div class="cf-list__titles">
        <h2 class="cf-list__title">{{ title }}</h2>
        <span class="cf-list__bar" aria-hidden="true"></span>
      </div>
      <span
        class="cf-list__count"
        v-if="!LoadingFlag && nominees.length"
        :aria-label="nominees.length + ' records'"
        >{{ nominees.length }}</span
      >
    </header>

    <!-- Loading Animation -->
    <v-overlay
      v-if="LoadingFlag"
      absolute
      color="#ffffff"
      opacity="0.8"
      class="loading-overlay"
    >
      <v-progress-circular
        indeterminate
        width="3"
        color="#3a67b1"
      ></v-progress-circular>
    </v-overlay>

    <!-- No Nominees Message -->
    <div v-else-if="!nominees.length" class="no-nominees">
      <span class="cf-empty__icon">
        <v-icon size="30">mdi-trophy-variant-outline</v-icon>
      </span>
      <p>No nominees found for this category</p>
    </div>

    <!-- Nominees Grid -->
    <v-row v-else class="nominees-container">
      <v-col
        v-for="(item, index) in nominees"
        :key="index"
        cols="12"
        sm="12"
        md="12"
        class="nominee-col"
        v-scroll-reveal="{
          delay: 200 * index,
          distance: '50px',
          duration: 800,
          origin: 'bottom',
        }"
      >
        <v-hover v-slot="{ hover }">
          <v-card
            flat
            class="withdrawal-card cf-candidate"
            :class="{ 'on-hover': hover }"
          >
            <!-- Card Header -->
            <div class="cf-candidate__main">
              <div class="cf-avatar">
                <v-avatar size="56">
                  <v-img
                    :src="item.MemberImage || defaultAvatarUrl"
                    :alt="item.MemberName"
                    @error="handleImageError"
                  >
                    <template v-slot:placeholder>
                      <v-avatar color="#3a67b1" size="56">
                        <span class="cf-avatar__initials">{{
                          getInitials(item.MemberName)
                        }}</span>
                      </v-avatar>
                    </template>
                  </v-img>
                </v-avatar>
                <span class="cf-avatar__badge" aria-hidden="true"
                  ><v-icon size="12" color="#0a0f29">mdi-crown</v-icon></span
                >
              </div>

              <div class="cf-candidate__info">
                <h3 class="cf-candidate__name">{{ item.MemberName }}</h3>
                <p class="cf-candidate__lo">
                  <v-icon x-small>mdi-map-marker-outline</v-icon>
                  <span>{{ item.LomName }}</span>
                </p>
              </div>
              <span class="cf-candidate__no">#{{ index + 1 }}</span>
            </div>

            <!-- button Section -->
            <div class="card-action-buttons">
              <v-tooltip bottom v-if="showApplicationButton">
                <template v-slot:activator="{ on, attrs }">
                  <a
                    class="action-button application-button"
                    :href="item.ApplicationPath"
                    target="_blank"
                    aria-label="View Application"
                    v-bind="attrs"
                    v-on="on"
                  >
                    <span class="button-icon">
                      <v-icon>mdi-file-pdf-box</v-icon>
                    </span>
                    <span class="button-text">Application</span>
                  </a>
                </template>
                <span>View Application</span>
              </v-tooltip>

              <v-tooltip bottom v-if="showScoreButton">
                <template v-slot:activator="{ on, attrs }">
                  <button
                    class="action-button score-button"
                    @click="addScore(item)"
                    v-bind="attrs"
                    v-on="on"
                    :disabled="item.ScoreSubmitted"
                  >
                    <span class="button-icon">
                      <v-icon>mdi-star</v-icon>
                    </span>
                    <span class="button-text">Score</span>
                  </button>
                </template>
              </v-tooltip>

              <v-tooltip bottom v-if="showAgeProofButton">
                <template v-slot:activator="{ on, attrs }">
                  <button
                    class="action-button age-proof-button"
                    @click="addAgeProof(item)"
                    v-bind="attrs"
                    v-on="on"
                    :disabled="item.AgeProofVerified"
                    :class="{ verified: item.AgeProofVerified }"
                  >
                    <span class="button-icon">
                      <v-icon>mdi-account-check</v-icon>
                    </span>
                    <span class="button-text">Age Proof</span>
                  </button>
                </template>
              </v-tooltip>

              <v-tooltip bottom v-if="ShowDisqualifyButton">
                <template v-slot:activator="{ on, attrs }">
                  <button
                    class="action-button disqualify-button"
                    @click="openDisqualifyDialog(item)"
                    v-bind="attrs"
                    v-on="on"
                    :disabled="item.Disqualified"
                    :class="{ verified: item.Disqualified }"
                  >
                    <span class="button-icon">
                      <v-icon>mdi-account-check</v-icon>
                    </span>
                    <span class="button-text">Disqualify</span>
                  </button>
                </template>
              </v-tooltip>
            </div>
          </v-card>
        </v-hover>
      </v-col>
    </v-row>

    <!-- Add Score Dialog -->
    <v-dialog
      v-model="addScorePrompt"
      max-width="75%"
      content-class="rounded-dialog"
    >
      <v-card class="rounded-lg">
        <add-withdrawal-score
          v-if="addScorePrompt"
          pageDescription="Update Nomination Score"
          :showDialog="addScorePrompt"
          @hideDialog="hideScorePrompt($event)"
          :row="selectedData"
        ></add-withdrawal-score>
      </v-card>
    </v-dialog>
    <!-- Disqualify Dialog -->
    <v-dialog v-model="disqualifyDialog" max-width="50%">
      <disqualified-approval
        pageDescription="Disqualify Candidate"
        :showDialog="disqualifyDialog"
        @hideDialog="hideDisqualifyDialog($event)"
        :row="selectedData"
        v-if="disqualifyDialog"
      />
    </v-dialog>

    <!-- Add Age Proof Dialog -->
    <v-dialog
      v-model="addAgeProofPrompt"
      max-width="75%"
      content-class="rounded-dialog"
    >
      <v-card class="rounded-lg">
        <age-proof-verification
          v-if="addAgeProofPrompt"
          pageDescription="Update Nomination Age Proof"
          :showDialog="addAgeProofPrompt"
          @hideDialog="hideAgeProofPrompt($event)"
          :row="selectedData"
        ></age-proof-verification>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import companyConfig from "@/company_config.json";

export default {
  name: "NominationList",
  props: {
    year: {
      type: Number,
      required: true,
    },
    zone: {
      type: Number,
      required: true,
    },
    title: {
      type: String,
      required: true,
    },
    designation: {
      type: Number,
      required: true,
    },
    type: {
      type: Number,
      required: true,
    },
    enableApplication: {
      type: Boolean,
      default: false,
    },
    enableScore: {
      type: Boolean,
      default: false,
    },
    enableAgeProof: {
      type: Boolean,
      default: false,
    },
    enableDisqualify: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      nominees: [],
      LoadingFlag: false,
      selectedData: null,
    };
  },
  methods: {
    getInitials(name) {
      return name
        .split(" ")
        .map((word) => word[0])
        .join("")
        .toUpperCase()
        .substr(0, 2);
    },
    handleImageError(error) {
      error.target.src = this.defaultAvatarUrl;
    },
    async getNomineesData() {
      console.log(
        "getNomineesData called for designation: " + this.designation
      );
      this.LoadingFlag = true;

      try {
        const server_url = companyConfig.apiURL;
        const token = this.$session.get("token") || "";
        const add_url = server_url + "api/nomination/lists";
        const upload = {
          UserInterface: 4,
          Designation: this.designation,
          Zone: this.zone,
          Year: this.year,
        };

        const response = await this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
        });

        if (response.data.flag == 1) {
          this.nominees = response.data.records.TableData;

          // Set visibility flags based on props
          this.showApplicationButton = this.enableApplication === true;
          // Only show score button if explicitly enabled
          this.showScoreButton = this.enableScore === true;
          // Only show age proof button if explicitly enabled
          this.showAgeProofButton = this.enableAgeProof === true;
          this.ShowDisqualifyButton = this.enableDisqualify === true; // Always show disqualify button
        } else {
          // clear previous zone/year records when nothing is found
          this.nominees = [];
        }
      } catch (error) {
        console.error(error);
      } finally {
        this.LoadingFlag = false;
      }
    },
  },
  created() {
    this.getNomineesData();
  },
  watch: {
    year() {
      this.getNomineesData();
    },
    zone() {
      this.getNomineesData();
    },
  },
};
</script>

<style lang="scss" scoped>
/* JCI approved palette */
.cf-list {
  --navy: #3a67b1;
  --aqua: #0097d7;
  --gold: #edbe38;
  --seafoam: #56bda3;
  --ink: #0a0f29;
  --text: #414042;
  --danger: #c53030;
  --muted: #6b7280;
  --line: #e3e9f3;
  --line-strong: #cdd8ea;
  --surface-tint: #f5f7fc;
  --accent: var(--navy);
  --accent-soft: rgba(58, 103, 177, 0.1);
  --ease: cubic-bezier(0.22, 0.61, 0.36, 1);
  --ease-out: cubic-bezier(0.16, 1, 0.3, 1);

  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 220px;
  color: var(--text);
  font-family: "Poppins", "Roboto", sans-serif;
}
.cf-list--navy {
  --accent: var(--navy);
  --accent-soft: rgba(58, 103, 177, 0.1);
  --head-bg: linear-gradient(120deg, var(--navy), var(--aqua));
  --head-ink: #ffffff;
}
.cf-list--danger {
  --accent: var(--danger);
  --accent-soft: rgba(197, 48, 48, 0.08);
  --head-bg: linear-gradient(120deg, var(--ink), var(--navy));
  --head-ink: #ffffff;
}
.cf-list--aqua {
  --accent: var(--aqua);
  --accent-soft: rgba(0, 151, 215, 0.1);
  --head-bg: linear-gradient(120deg, var(--aqua), var(--seafoam));
  --head-ink: #ffffff;
}
.cf-list--slate {
  --accent: #5b6b86;
  --accent-soft: rgba(91, 107, 134, 0.1);
  --head-bg: linear-gradient(120deg, var(--navy), var(--seafoam));
  --head-ink: #ffffff;
}
.cf-list--gold {
  --accent: #c99a17;
  --accent-soft: rgba(237, 190, 56, 0.16);
  --head-bg: linear-gradient(120deg, var(--gold), rgba(237, 190, 56, 0.82));
  --head-ink: var(--ink);
}

/* ---------- Header (coloured band, JCI approved colours) ---------- */
.cf-list__head {
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  margin-bottom: 14px;
  border-radius: 16px;
  color: var(--head-ink);
  background: var(--head-bg);
  box-shadow: 0 12px 24px -16px rgba(10, 15, 41, 0.6);

  &::after {
    /* fine grid texture */
    content: "";
    position: absolute;
    inset: 0;
    background-image: linear-gradient(
        rgba(255, 255, 255, 0.08) 1px,
        transparent 1px
      ),
      linear-gradient(90deg, rgba(255, 255, 255, 0.08) 1px, transparent 1px);
    background-size: 22px 22px;
    mask-image: linear-gradient(90deg, transparent 30%, #000 95%);
    -webkit-mask-image: linear-gradient(90deg, transparent 30%, #000 95%);
    pointer-events: none;
  }
}
.cf-list__icon {
  position: relative;
  z-index: 1;
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.32);
  transition: transform 280ms var(--ease);

  .v-icon {
    color: var(--head-ink) !important;
  }
}
.cf-list:hover .cf-list__icon {
  transform: rotate(-6deg) scale(1.05);
}
.cf-list__titles {
  position: relative;
  z-index: 1;
  flex: 1 1 auto;
  min-width: 0;
}
.cf-list__title {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 700;
  line-height: 1.3;
  color: var(--head-ink);
  overflow-wrap: anywhere;
}
.cf-list__bar {
  display: block;
  width: 36px;
  height: 3px;
  margin-top: 6px;
  border-radius: 3px;
  background: var(--head-ink);
  opacity: 0.75;
  transform-origin: left center;
  animation: cf-grow 700ms var(--ease-out) 150ms backwards;
}
.cf-list__count {
  position: relative;
  z-index: 1;
  flex: 0 0 auto;
  min-width: 34px;
  height: 28px;
  padding: 0 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  font-size: 0.82rem;
  font-weight: 700;
  color: var(--ink);
  background: #ffffff;
  box-shadow: 0 6px 14px -8px rgba(10, 15, 41, 0.6);
  font-variant-numeric: tabular-nums;
}

/* ---------- Loading & empty ---------- */
.loading-overlay {
  border-radius: 16px;
}
.no-nominees {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 28px 16px;
  text-align: center;
  border: 1.5px dashed var(--line-strong);
  border-radius: 16px;
  background: var(--surface-tint);
  animation: cf-fade 500ms var(--ease-out);

  p {
    margin: 12px 0 0;
    font-size: 0.88rem;
    font-weight: 500;
    color: var(--muted);
  }
}
.cf-empty__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: #ffffff;
  border: 1px solid var(--line);
  box-shadow: 0 10px 24px -14px rgba(58, 103, 177, 0.6);

  .v-icon {
    color: var(--accent) !important;
    opacity: 0.75;
  }
}

/* ---------- Candidate list ---------- */
.nominees-scroll {
  flex: 1;
  min-height: 0;
}
.nominees-container {
  flex: 1;
  margin: 0;
  padding: 2px 4px 2px 0;
  max-height: 420px;
  overflow-y: auto;
  overscroll-behavior: contain;
  scrollbar-width: thin;
  scrollbar-color: rgba(58, 103, 177, 0.3) transparent;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background-color: rgba(58, 103, 177, 0.3);
    border-radius: 3px;
  }
}
.nominee-col {
  padding: 0 0 10px;

  &:last-child {
    padding-bottom: 0;
  }
}
.cf-candidate.v-card {
  position: relative;
  overflow: hidden;
  border-radius: 16px !important;
  border: 1px solid var(--line) !important;
  background: #ffffff;
  box-shadow: 0 1px 2px rgba(10, 15, 41, 0.04),
    0 4px 12px rgba(58, 103, 177, 0.05) !important;
  transition: transform 280ms var(--ease), box-shadow 280ms var(--ease),
    border-color 280ms var(--ease);

  &::before {
    content: "";
    position: absolute;
    left: 0;
    top: 14px;
    bottom: 14px;
    width: 4px;
    border-radius: 0 4px 4px 0;
    background: var(--accent);
    opacity: 0.85;
    transition: top 280ms var(--ease), bottom 280ms var(--ease);
  }
  &.on-hover {
    transform: translateY(-2px);
    border-color: var(--line-strong) !important;
    box-shadow: 0 14px 28px -16px rgba(58, 103, 177, 0.55) !important;

    &::before {
      top: 0;
      bottom: 0;
    }
    .cf-avatar {
      transform: scale(1.04);
    }
  }
}
.cf-candidate__main {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px 14px 20px;
}
.cf-avatar {
  position: relative;
  flex: 0 0 auto;
  padding: 3px;
  border-radius: 50%;
  background: conic-gradient(
    from 200deg,
    var(--accent),
    var(--aqua),
    var(--seafoam),
    var(--accent)
  );
  transition: transform 280ms var(--ease);

  .v-avatar {
    border: 2px solid #ffffff;
    background: var(--surface-tint);
  }
}
.cf-avatar__initials {
  font-size: 1.05rem;
  font-weight: 700;
  color: #ffffff;
}
.cf-avatar__badge {
  position: absolute;
  right: -2px;
  bottom: -2px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: var(--gold);
  border: 2px solid #ffffff;
  box-shadow: 0 4px 10px -4px rgba(237, 190, 56, 0.9);
}
.cf-candidate__info {
  flex: 1 1 auto;
  min-width: 0;
}
.cf-candidate__name {
  margin: 0;
  font-size: 0.98rem;
  font-weight: 600;
  line-height: 1.35;
  color: var(--ink);
  overflow-wrap: anywhere;
}
.cf-candidate__lo {
  display: flex;
  align-items: center;
  gap: 4px;
  margin: 3px 0 0;
  font-size: 0.82rem;
  color: var(--muted);
  overflow-wrap: anywhere;

  .v-icon {
    color: var(--accent) !important;
  }
}
.cf-candidate__no {
  flex: 0 0 auto;
  align-self: flex-start;
  font-size: 0.72rem;
  font-weight: 700;
  color: var(--muted);
  padding: 3px 8px;
  border-radius: 999px;
  background: var(--surface-tint);
  font-variant-numeric: tabular-nums;
}

/* ---------- Actions ---------- */
.card-action-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 10px 16px 12px 20px;
  border-top: 1px dashed var(--line);
  background: linear-gradient(180deg, #ffffff, var(--surface-tint));

  /* hidden when no action is enabled (whitespace keeps :empty from matching) */
  &:empty,
  &:not(:has(*)) {
    display: none;
  }
}
.action-button {
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 32px !important;
  min-width: 0 !important;
  padding: 0 12px !important;
  border-radius: 10px !important;
  border: 1px solid transparent;
  font-family: inherit;
  font-size: 0.78rem !important;
  font-weight: 600 !important;
  letter-spacing: 0.02em !important;
  text-transform: none !important;
  text-decoration: none !important;
  cursor: pointer;
  transition: transform 180ms var(--ease), box-shadow 180ms var(--ease),
    background-color 180ms var(--ease);

  .v-icon {
    font-size: 17px !important;
    margin: 0 !important;
  }
  .button-icon {
    display: inline-flex;
  }
  .button-text {
    white-space: nowrap;
  }
  &:hover:not(:disabled):not(.v-btn--disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 16px -10px rgba(10, 15, 41, 0.45);
  }
  &:focus-visible {
    outline: 3px solid rgba(0, 151, 215, 0.45);
    outline-offset: 2px;
  }
  &:disabled,
  &.v-btn--disabled {
    cursor: default;
  }
}
.application-button {
  background: rgba(197, 48, 48, 0.08) !important;
  border-color: rgba(197, 48, 48, 0.3);
  color: var(--danger) !important;

  .v-icon {
    color: var(--danger) !important;
  }
}
.score-button {
  background: var(--navy) !important;
  color: #ffffff !important;

  .v-icon {
    color: var(--gold) !important;
  }
  &:disabled,
  &.v-btn--disabled {
    background: rgba(86, 189, 163, 0.14) !important;
    border-color: rgba(86, 189, 163, 0.5);
    color: #2f7d69 !important;

    .v-icon {
      color: #2f7d69 !important;
    }
  }
}
.age-proof-button {
  background: #ffffff !important;
  border-color: var(--navy);
  color: var(--navy) !important;

  .v-icon {
    color: var(--navy) !important;
  }
  &.verified,
  &:disabled,
  &.v-btn--disabled {
    background: rgba(86, 189, 163, 0.14) !important;
    border-color: rgba(86, 189, 163, 0.5);
    color: #2f7d69 !important;

    .v-icon {
      color: #2f7d69 !important;
    }
  }
}
.disqualify-button {
  background: var(--danger) !important;
  color: #ffffff !important;

  .v-icon {
    color: #ffffff !important;
  }
  &.verified,
  &:disabled,
  &.v-btn--disabled {
    background: rgba(197, 48, 48, 0.08) !important;
    border-color: rgba(197, 48, 48, 0.35);
    color: var(--danger) !important;

    .v-icon {
      color: var(--danger) !important;
    }
  }
}

@media (max-width: 599px) {
  .cf-candidate__main {
    padding: 12px 12px 12px 16px;
    gap: 12px;
  }
  .card-action-buttons {
    padding: 10px 12px 12px 16px;
  }
  .action-button {
    flex: 1 1 auto;
  }
  .nominees-container {
    max-height: 360px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .cf-list *,
  .cf-list *::before {
    animation: none !important;
    transition: none !important;
  }
}

@keyframes cf-grow {
  from {
    transform: scaleX(0);
  }
  to {
    transform: scaleX(1);
  }
}
@keyframes cf-fade {
  from {
    opacity: 0;
    transform: translateY(6px);
  }
  to {
    opacity: 1;
    transform: none;
  }
}
</style>
